import React, {useState} from 'react';
import {Leiter, Participant} from '../../types';

interface Step2Props {
    onSubmit: (participants: Participant[]) => void;
    onBack: (currentParticipants: Participant[]) => void;
    initialData: Participant[];
    leiterData: Leiter;
}

const ParticipantEntry: React.FC<{
    index: number;
    participant: Participant;
    onChange: (index: number, field: keyof Participant, value: string) => void;
    onEmailBlur: (index: number, email: string) => void;
    errors: Partial<Participant>;
}> = ({index, participant, onChange, onEmailBlur, errors}) => {

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        onChange(index, e.target.name as keyof Participant, e.target.value);
    };

    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        onEmailBlur(index, e.target.value);
    };

    const getInputClass = (fieldName: keyof Participant) =>
        `mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900 ${errors[fieldName] ? 'border-red-500' : ''}`;

    return (
        <div className="participant-entry p-4 border border-gray-200 rounded-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Teilnehmender {index + 1}</h2>
            <div className="space-y-4">
                <div>
                    <label htmlFor={`anrede-${index}`}
                           className="block text-sm font-medium text-gray-700">Anrede</label>
                    <select id={`anrede-${index}`} name="anrede" value={participant.anrede} onChange={handleChange}
                            className={getInputClass('anrede')}>
                        <option value="" disabled>Bitte wählen Sie eine Anrede</option>
                        <option value="frau">Frau</option>
                        <option value="herr">Herr</option>
                        <option value="divers">Divers</option>
                    </select>
                    {errors.anrede && <p className="mt-1 text-sm text-red-600">{errors.anrede}</p>}
                </div>
                <div>
                    <label htmlFor={`titel-${index}`} className="block text-sm font-medium text-gray-700">Titel (optional)</label>
                    <input type="text" id={`titel-${index}`} name="titel" value={participant.titel} onChange={handleChange} className={getInputClass('titel')} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor={`vorname-${index}`}
                               className="block text-sm font-medium text-gray-700">Vorname</label>
                        <input type="text" id={`vorname-${index}`} name="vorname" value={participant.vorname}
                               onChange={handleChange} className={getInputClass('vorname')}/>
                        {errors.vorname && <p className="mt-1 text-sm text-red-600">{errors.vorname}</p>}
                    </div>
                    <div>
                        <label htmlFor={`nachname-${index}`}
                               className="block text-sm font-medium text-gray-700">Nachname</label>
                        <input type="text" id={`nachname-${index}`} name="nachname" value={participant.nachname}
                               onChange={handleChange} className={getInputClass('nachname')}/>
                        {errors.nachname && <p className="mt-1 text-sm text-red-600">{errors.nachname}</p>}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor={`email-${index}`}
                               className="block text-sm font-medium text-gray-700">E-Mail-Adresse</label>
                        <input type="email" id={`email-${index}`} name="email" value={participant.email}
                               onChange={handleChange} onBlur={handleEmailBlur} className={getInputClass('email')}/>
                        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>
                    <div>
                        <label htmlFor={`position-${index}`} className="block text-sm font-medium text-gray-700">Position
                            im Unternehmen</label>
                        <input type="text" id={`position-${index}`} name="position" value={participant.position}
                               onChange={handleChange} className={getInputClass('position')}/>
                        {errors.position && <p className="mt-1 text-sm text-red-600">{errors.position}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

const getInitialParticipants = (data: Participant[]): Participant[] => {
    if (data.length > 0) return data;
    return [];
};

const Step2ParticipantsForm: React.FC<Step2Props> = ({leiterData, onSubmit, onBack, initialData}) => {
    const initialParticipants = getInitialParticipants(initialData);
    const [participants, setParticipants] = useState<Participant[]>(initialParticipants);
    const [errors, setErrors] = useState<Partial<Participant>[]>(() => Array.from({length: initialParticipants.length}, () => ({})));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleCountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCount = parseInt(e.target.value, 10);

        setParticipants(currentParticipants => {
            const updatedParticipants = [...currentParticipants];
            while (updatedParticipants.length < newCount) {
                updatedParticipants.push({anrede: '', titel: '', vorname: '', nachname: '', email: '', position: ''});
            }
            return updatedParticipants.slice(0, newCount);
        });

        setErrors(currentErrors => {
            const updatedErrors = [...currentErrors];
            while (updatedErrors.length < newCount) {
                updatedErrors.push({});
            }
            return updatedErrors.slice(0, newCount);
        });
    };

    const checkEmailExists = async (email: string): Promise<string | null> => {
        if (!email) {
            return 'Bitte geben Sie eine E-Mail-Adresse an.';
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            return 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
        }
        if (email.toLowerCase() === leiterData.email.toLowerCase()) {
            return 'Diese E-Mail-Adresse ist identisch mit der des Standortleiters.';
        }
        try {
            const res = await fetch('/api/standortleiter/check-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!res.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await res.json();
            if (data.exists) {
                return 'Diese E-Mail-Adresse ist bereits registriert.';
            }
        } catch (error) {
            console.error('Email validation error:', error);
            return 'Fehler bei der E-Mail-Validierung.';
        }
        return null;
    };

    const handleParticipantChange = (index: number, field: keyof Participant, value: string) => {
        const updatedParticipants = [...participants];
        updatedParticipants[index] = {...updatedParticipants[index], [field]: value};
        setParticipants(updatedParticipants);

        if (errors[index]?.[field]) {
            const newErrors = [...errors];
            const participantErrors = {...newErrors[index]};
            let fieldIsValid = false;

            switch (field) {
                case 'anrede':
                case 'vorname':
                case 'nachname':
                    fieldIsValid = value.trim() !== '';
                    break;
                case 'position':
                    fieldIsValid = value !== '';
                    break;
                case 'email':
                    fieldIsValid = /\S+@\S+\.\S+/.test(value);
                    break;
                default:
                    break;
            }

            if (fieldIsValid) {
                delete participantErrors[field];
                newErrors[index] = participantErrors;
                setErrors(newErrors);
            }
        }
    };

    const handleEmailBlur = async (index: number, email: string) => {
        const emailError = await checkEmailExists(email);
        if (emailError) {
            const newErrors = [...errors];
            newErrors[index] = {...newErrors[index], email: emailError};
            setErrors(newErrors);
        }
    };

    const validate = async (): Promise<boolean> => {
        let isValid = true;
        const newErrors = await Promise.all(participants.map(async (p) => {
            const participantErrors: Partial<Participant> = {};
            if (!p.anrede) participantErrors.anrede = 'Bitte wählen Sie eine Anrede aus.';
            if (!p.vorname) participantErrors.vorname = 'Bitte geben Sie einen Vornamen an.';
            if (!p.nachname) participantErrors.nachname = 'Bitte geben Sie einen Nachnamen an.';
            if (!p.position) participantErrors.position = 'Bitte wählen Sie eine Position aus.';
            
            const emailError = await checkEmailExists(p.email);
            if (emailError) {
                participantErrors.email = emailError;
            }

            if (Object.keys(participantErrors).length > 0) isValid = false;
            return participantErrors;
        }));
        setErrors(newErrors);
        return isValid;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        if (await validate()) {
            setIsSubmitting(true);

            const payload = {
                standortleiter: {
                    salutation: leiterData.anrede,
                    title: leiterData.titel,
                    firstName: leiterData.vorname,
                    lastName: leiterData.nachname,
                    email: leiterData.email,
                    standort: leiterData.standort,
                },
                invitees: participants.map(p => ({
                    salutation: p.anrede,
                    title: p.titel,
                    firstName: p.vorname,
                    lastName: p.nachname,
                    email: p.email,
                    position: p.position,
                })),
            };

            try {
                const response = await fetch('/api/standortleiter', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to submit registration.');
                }

                onSubmit(participants);

            } catch (error: any) {
                console.error('Submission error:', error);
                setSubmitError(error.message || 'An unexpected error occurred.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleBack = () => {
        onBack(participants);
    };

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
            <div className="mb-6">
                <label htmlFor="participant-count" className="block text-sm font-medium text-gray-700">Wie viele
                    Teilnehmende möchten Sie registrieren?</label>
                <p className="text-xs text-gray-500 mb-2">Bitte beachten Sie die Auswahlkriterien aus der Einladungsmail zur Anmeldung.</p>
                <select id="participant-count" name="participant-count" value={participants.length}
                        onChange={handleCountChange}
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900">
                    {[...Array(11)].map((_, i) => (
                        <option key={i} value={i}>{i}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                    {participants.map((p, i) => (
                        <ParticipantEntry
                            key={i}
                            index={i}
                            participant={p}
                            onChange={handleParticipantChange}
                            onEmailBlur={handleEmailBlur}
                            errors={errors[i] || {}}
                        />
                    ))}
                </div>
                {submitError && <p className="mt-4 text-sm text-red-600 text-center">{submitError}</p>}
                <div className="mt-8 border-t pt-6 flex flex-col md:flex-row-reverse gap-4">
                    <button type="submit" disabled={isSubmitting}
                            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-magenta hover:bg-berry focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-magenta disabled:opacity-50">
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Wird gesendet...</span>
                            </>
                        ) : (
                            'Registrierung abschließen'
                        )}
                    </button>
                    <button type="button" onClick={handleBack} disabled={isSubmitting}
                            className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-custom disabled:opacity-50">
                        Zurück
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Step2ParticipantsForm;